import { useSyncExternalStore } from "react";

export interface PerformanceSnapshot {
  backend: string;
  connectionState: string;
  reconnectCount: number;
  codec?: string;
  decoderImplementation?: string;
  powerEfficientDecoder?: boolean;
  width?: number;
  height?: number;
  fps?: number;
  framesReceived?: number;
  framesDecoded?: number;
  framesDropped?: number;
  droppedFramePercent?: number;
  keyFramesDecoded?: number;
  totalDecodeTimeMs?: number;
  averageDecodeTimeMs?: number;
  decoderQueueSize?: number;
  jitterMs?: number;
  jitterBufferDelayMs?: number;
  averageJitterBufferResidenceMs?: number;
  freezeCount?: number;
  totalFreezeDurationMs?: number;
  maxFreezeDurationMs?: number;
  frameGapCount?: number;
  lastFrameGapMs?: number;
  maxFrameGapMs?: number;
  packetsLost?: number;
  bytesReceived?: number;
  bitrateMbps?: number;
  localCandidateType?: string;
  remoteCandidateType?: string;
  protocol?: string;
  relayProtocol?: string;
  rttMs?: number;
  availableIncomingBitrateMbps?: number;
  icePath?: string;
  dataChannelState?: string;
  dataMessagesSent?: number;
  dataMessagesReceived?: number;
  dataBytesSent?: number;
  dataBytesReceived?: number;
  dataBufferedAmount?: number;
  audioState?: string;
  audioCodec?: string;
  audioBytesReceived?: number;
  audioBufferedSeconds?: number;
  audioReconnectCount?: number;
  audioDroppedBytes?: number;
  audioBufferingCount?: number;
  audioBufferingTotalMs?: number;
  audioBufferingMaxMs?: number;
  touchToVisibleMs?: number;
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
  androidRuntime?: string;
  androidRenderer?: string;
  senderCodec?: string;
  senderEncoder?: string;
  senderEncoderType?: string;
  configuredFps?: number;
  capturedFps?: number;
  encodedFps?: number;
  streamQueueDepth?: number;
  streamGatewayDrops?: number;
  connectionMode?: string;
  foregroundPackage?: string;
  relayDisplayReconnects?: number;
  relayAudioReconnects?: number;
  relayDisplayGapCount?: number;
  relayAudioGapCount?: number;
  relayDisplayMaxGapMs?: number;
  relayAudioMaxGapMs?: number;
  relayDisplaySlowSends?: number;
  relayAudioSlowSends?: number;
  relayDisplayMaxSendWaitMs?: number;
  relayAudioMaxSendWaitMs?: number;
  emulatorCpuPercent?: number;
  gatewayCpuPercent?: number;
  aiCpuPercent?: number;
}

let snapshot: PerformanceSnapshot = {
  backend: "native-webrtc",
  connectionState: "connecting",
  reconnectCount: 0,
  longTaskCount: 0,
  longTaskTotalMs: 0,
  longTaskMaxMs: 0,
};
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export const performanceStore = {
  getSnapshot: () => snapshot,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  update: (values: Partial<PerformanceSnapshot>) => {
    snapshot = { ...snapshot, ...values };
    emit();
  },
};

export const usePerformanceSnapshot = () => useSyncExternalStore(
  performanceStore.subscribe,
  performanceStore.getSnapshot,
  performanceStore.getSnapshot,
);

if (typeof window !== "undefined" && "PerformanceObserver" in window) {
  try {
    const observer = new PerformanceObserver((list) => {
      let count = snapshot.longTaskCount;
      let total = snapshot.longTaskTotalMs;
      let maximum = snapshot.longTaskMaxMs;
      for (const entry of list.getEntries()) {
        count += 1;
        total += entry.duration;
        maximum = Math.max(maximum, entry.duration);
      }
      performanceStore.update({
        longTaskCount: count,
        longTaskTotalMs: Math.round(total * 10) / 10,
        longTaskMaxMs: Math.round(maximum * 10) / 10,
      });
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch { /* Long Tasks are not available in every browser. */ }
}

declare global {
  interface Window {
    __is4fPerformance?: {
      setTouchToVisible: (milliseconds: number) => void;
      snapshot: () => PerformanceSnapshot;
    };
  }
}

if (typeof window !== "undefined") {
  window.__is4fPerformance = {
    setTouchToVisible: (touchToVisibleMs) => performanceStore.update({ touchToVisibleMs }),
    snapshot: performanceStore.getSnapshot,
  };
}
